import { type FC, useEffect, useState } from 'react';
import {
    Box,
    Flex,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    useBreakpointValue,
} from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useGetTurnToQuestions } from '../../bff-api/turn-to/useGetTurnToQuestions';
import { useTurnToProductData } from '../../bff-api/turn-to/useTurnToProductData';
import { useTurnTo } from '../../contexts/useTurnTo';
import { Button, Rating, Text } from '../../ui-components';
import { Accordion, AccordionItem } from '../../ui-components/accordion';
import { Filters, ProductReview, QuestionsHeader, QuestionsList, ReviewItem } from './components';
import { TURN_TO_DEFAULT_LOAD_LIMIT } from './const';
import './turn-to.scss';
import type { ReviewTypes } from '../../bff-api/turn-to/model';
import type { ReviewFilters, SortOptions } from './types';

interface ReviewListProps {
    reviewsList: ReviewTypes[];
}
const ReviewList: FC<ReviewListProps> = ({ reviewsList }) => {
    if (!reviewsList.length) {
        return (
            <Text>
                <FormattedMessage
                    defaultMessage="No items match the entered criteria."
                    id="turn-to.no-reviews"
                />
            </Text>
        );
    }

    return (
        <Box background="white" paddingX={{ base: 0, 'lg-sm': 4 }}>
            {reviewsList?.map(
                ({
                    id,
                    text,
                    dateCreated,
                    title,
                    user,
                    rating,
                    upVotes,
                    downVotes,
                    inappropriateFlag,
                    media,
                    syndication,
                }) => (
                    <ReviewItem
                        key={id}
                        dateCreated={dateCreated}
                        syndication={syndication}
                        text={text}
                        title={title}
                        rating={rating}
                        upVotes={upVotes}
                        downVotes={downVotes}
                        inappropriateFlag={inappropriateFlag}
                        id={id}
                        photoList={media.photo}
                        user={user}
                    />
                ),
            )}
        </Box>
    );
};

export interface TurnToProps {
    productId?: string;
}

export const TurnTo: FC<TurnToProps> = ({ productId }) => {
    const [reviewsList, setReviewsList] = useState<ReviewTypes[]>([]);
    const [offset, setOffset] = useState<number>(0);
    const [sortOption, setSortOption] = useState<SortOptions>('mostRecent');
    const [searchString, setSearchString] = useState<string>('');
    const [filtersList, setFilters] = useState<ReviewFilters[]>([]);
    const generateFilterString = (): string =>
        filtersList.length ? `rating(${filtersList.join()})` : '';
    const { questionsNumber, questionsOffset, questionsSortOption, showMoreQuestions, reviewsRef } =
        useTurnTo();
    const { data, refetch } = useTurnToProductData(
        productId,
        TURN_TO_DEFAULT_LOAD_LIMIT,
        offset,
        sortOption,
        searchString,
        generateFilterString(),
    );

    const { data: questionData } = useGetTurnToQuestions(
        productId,
        TURN_TO_DEFAULT_LOAD_LIMIT,
        offset,
        questionsSortOption,
    );

    useEffect(() => {
        if (data && offset === 0) {
            setReviewsList(data.reviewsList.reviews);
        } else if (data) {
            setReviewsList((prevReviewList) => [...prevReviewList, ...data.reviewsList.reviews]);
        }
    }, [data]);

    useEffect(() => {
        const refetchData = async (): Promise<void> => {
            await refetch();
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        refetchData();
    }, [offset, sortOption, searchString, refetch, filtersList]);

    const device = useBreakpointValue<'mobile' | 'desktop'>({
        base: 'mobile',
        'lg-sm': 'desktop',
    });

    if (!data || !productId) {
        return null;
    }

    const showMore = (): void => {
        setOffset((currentOffset) => currentOffset + TURN_TO_DEFAULT_LOAD_LIMIT);
    };

    const removeFilter = (filterName: ReviewFilters): void => {
        setFilters(filtersList.filter((filter) => filter !== filterName));
    };

    const clearFilters = (): void => {
        setFilters([]);
    };

    const onFilter = (value: ReviewFilters | null): void => {
        if (value) {
            if (filtersList.includes(value)) {
                removeFilter(value);
            } else {
                setFilters((prevFilters) => [...prevFilters, value]);
            }
        }
    };

    const { productSummary, reviewsList: reviews } = data;

    return (
        <Box
            paddingX={{ base: 4, sm: 8, lg: 10 }}
            background="white"
            ref={reviewsRef || null}
            data-testid="turn-to"
        >
            {device === 'mobile' ? (
                <Accordion defaultIndex={0}>
                    <AccordionItem
                        title={
                            <Box display="inline-flex" gap="1rem" alignItems="center">
                                <Text style="bold">
                                    <FormattedMessage
                                        defaultMessage="Read reviews ({reviewsNumber})"
                                        id="turn-to.read-reviews"
                                        values={{ reviewsNumber: reviews.total }}
                                    />
                                </Text>
                                <Rating
                                    initialRating={productSummary.averageRating}
                                    disabled
                                    iconSize="sm"
                                />
                            </Box>
                        }
                        titleTag="h2"
                    >
                        <ProductReview productSummary={productSummary} productId={productId} />
                        {productSummary.reviews > 0 && (
                            <>
                                <Filters
                                    onSort={(value: SortOptions) => setSortOption(value)}
                                    sortOption={sortOption}
                                    onFilter={onFilter}
                                    onSearch={(value: string) => setSearchString(value)}
                                    ratingBreakdown={productSummary.ratingBreakdown}
                                    filtersList={filtersList}
                                    removeFilter={removeFilter}
                                    totalNumberReviews={data.reviewsList.total}
                                    clearFilters={clearFilters}
                                />

                                <ReviewList reviewsList={reviewsList} />
                                {reviews.total - offset > 5 && (
                                    <Box marginTop={8} marginBottom={4}>
                                        <Button variant="secondary" onClick={showMore} wide>
                                            <Text type="all-caps">
                                                <FormattedMessage
                                                    defaultMessage="Show more"
                                                    id="turn-to.show-more"
                                                />
                                            </Text>
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                    </AccordionItem>

                    <AccordionItem
                        title={
                            <Box display="inline-flex" gap="1rem">
                                <Box display="inline-flex" gap="0.5rem">
                                    <Text style="bold">
                                        <FormattedMessage
                                            defaultMessage="Questions & Answers ({questionsNumber})"
                                            id="turn-to.questions-and-answers-count"
                                            values={{ questionsNumber }}
                                        />
                                    </Text>
                                </Box>
                            </Box>
                        }
                        titleTag="h2"
                    >
                        <QuestionsHeader
                            questionsList={questionData?.questions ?? []}
                            productId={productId}
                        />
                        <QuestionsList />
                        {questionsNumber - questionsOffset > 5 && (
                            <Box margin={4}>
                                <Button variant="secondary" onClick={showMoreQuestions} wide>
                                    <Text type="all-caps">
                                        <FormattedMessage
                                            defaultMessage="Show more"
                                            id="turn-to.show-more"
                                        />
                                    </Text>
                                </Button>
                            </Box>
                        )}
                    </AccordionItem>
                </Accordion>
            ) : (
                <Tabs variant="slt-turn-to">
                    <TabList>
                        <Tab>
                            <Text as="h2" color="slt-pepper.1000" style="inherit">
                                <FormattedMessage
                                    defaultMessage="Read reviews ({reviewsNumber})"
                                    id="turn-to.read-reviews"
                                    values={{ reviewsNumber: reviews.total }}
                                />
                            </Text>
                        </Tab>
                        <Tab>
                            <Text as="h2" color="slt-pepper.1000" style="inherit">
                                <FormattedMessage
                                    defaultMessage="Questions & Answers ({questionsNumber})"
                                    id="turn-to.q&a"
                                    values={{ questionsNumber }}
                                />
                            </Text>
                        </Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel
                            padding={8}
                            margin="0"
                            background={productSummary.reviews > 0 ? 'slt-grey.100' : 'white'}
                        >
                            <ProductReview productSummary={productSummary} productId={productId} />
                            {productSummary.reviews > 0 && (
                                <>
                                    <Filters
                                        onSort={(value: SortOptions) => setSortOption(value)}
                                        sortOption={sortOption}
                                        onFilter={onFilter}
                                        onSearch={(value: string) => setSearchString(value)}
                                        ratingBreakdown={productSummary.ratingBreakdown}
                                        filtersList={filtersList}
                                        removeFilter={removeFilter}
                                        totalNumberReviews={data.reviewsList.total}
                                        clearFilters={clearFilters}
                                    />
                                    <ReviewList reviewsList={reviewsList} />
                                    {reviews.total - offset > 5 && (
                                        <Flex marginTop={8} justifyContent="center">
                                            <Button variant="secondary" onClick={showMore}>
                                                <Text type="all-caps" style="bold">
                                                    <FormattedMessage
                                                        defaultMessage="Show more"
                                                        id="turn-to.show-more"
                                                    />
                                                </Text>
                                            </Button>
                                        </Flex>
                                    )}
                                </>
                            )}
                        </TabPanel>
                        <TabPanel padding={8} margin="0" background="slt-grey.100">
                            <QuestionsHeader
                                questionsList={questionData?.questions ?? []}
                                productId={productId}
                            />
                            <QuestionsList />
                            {questionsNumber - questionsOffset > 5 && (
                                <Flex marginTop={8} marginBottom={4} justifyContent="center">
                                    <Button variant="secondary" onClick={showMoreQuestions}>
                                        <FormattedMessage
                                            defaultMessage="Show more"
                                            id="turn-to.show-more"
                                        />
                                    </Button>
                                </Flex>
                            )}
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            )}
        </Box>
    );
};

TurnTo.defaultProps = {
    productId: undefined,
};
